import { cn } from "@/utils";
import React from "react";

interface LogoIconProps {
  className?: string;
  size?: number;
  width?: number;
  height?: number;
}

/**
 * Custom logo icon for the GenAI Jury application header
 * Sparkle/scale motif SVG with blue gradient tones
 */
export const LogoIcon: React.FC<LogoIconProps> = ({
  className,
  size = 20,
  width = size,
  height = size,
}) => {
  return (
    <svg
      id="Layer_1"
      xmlns="http://www.w3.org/2000/svg"
      xmlnsXlink="http://www.w3.org/1999/xlink"
      width={width}
      height={height}
      viewBox="0 0 32 32"
      className={cn("inline-block", className)}
    >
      <defs>
        <linearGradient
          id="logo-lg-1"
          x1="1.32"
          y1="17.163"
          x2="8.305"
          y2="24.148"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0" stopColor="#0193cc" />
          <stop offset=".244" stopColor="#0a95cf" />
          <stop offset=".567" stopColor="#1e9cd7" />
          <stop offset=".865" stopColor="#0075be" />
          <stop offset=".994" stopColor="#005b97" />
        </linearGradient>
        <linearGradient
          id="logo-lg-2"
          x1="0"
          y1="24.157"
          x2="9.625"
          y2="24.157"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0" stopColor="#0186c2" />
          <stop offset=".771" stopColor="#0165a2" />
          <stop offset=".994" stopColor="#015b98" />
        </linearGradient>
        <linearGradient
          id="logo-lg-3"
          x1="23.695"
          y1="17.163"
          x2="30.68"
          y2="24.148"
          xlinkHref="#logo-lg-1"
        />
        <linearGradient
          id="logo-lg-4"
          x1="22.375"
          y1="24.157"
          x2="32"
          y2="24.157"
          xlinkHref="#logo-lg-2"
        />
        <linearGradient
          id="logo-lg-5"
          x1="18.094"
          y1="13.723"
          x2="28.498"
          y2="13.723"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0" stopColor="#007ba4" />
          <stop offset=".17" stopColor="#009ecb" />
          <stop offset="1" stopColor="#0272af" />
        </linearGradient>
        <radialGradient
          id="logo-rg-1"
          cx="13.287"
          cy="15.997"
          fx="13.287"
          fy="9.038"
          r="12.666"
          gradientTransform="translate(1.238 -5.859) rotate(3.299) scale(1 1.321)"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0" stopColor="#c4fff9" />
          <stop offset="1" stopColor="#00386a" />
        </radialGradient>
        <style>
          {`
          .logo-sparkle {
            animation: sparkle-glow 2.5s ease-in-out infinite;
            filter: drop-shadow(0 0 0px rgba(196, 255, 249, 0));
          }

          @keyframes sparkle-glow {
            0%, 100% {
              filter: drop-shadow(0 0 0px rgba(196, 255, 249, 0));
              opacity: 1;
            }
            50% {
              filter: drop-shadow(0 0 0.5px rgba(196, 255, 249, 0.3));
              opacity: 0.9;
            }
          }
          `}
        </style>
      </defs>
      {/* Left scale */}
      <path
        d="M9.62,22.833h-.817l-3.99-7.964-3.991,7.964H.006l4.373-8.728h.868l4.373,8.728Z"
        fill="url(#logo-lg-1)"
      />
      <path
        d="M9.625,22.833c-.475,1.513-2.449,2.647-4.812,2.647s-4.338-1.134-4.813-2.647h9.625Z"
        fill="url(#logo-lg-2)"
      />
      {/* Right scale */}
      <path
        d="M31.994,22.833h-.817l-3.99-7.964-3.991,7.964h-.817l4.373-8.728h.868l4.373,8.728Z"
        fill="url(#logo-lg-3)"
      />
      <path
        d="M32,22.833c-.475,1.513-2.449,2.647-4.812,2.647s-4.338-1.134-4.813-2.647h9.625Z"
        fill="url(#logo-lg-4)"
      />
      {/* Balance beam */}
      <path
        d="M28.498,13.547h0c0,.333-.27.603-.603.603h-8.456c-.026-.004-.05.005-.078.004-.071-.003-.15-.004-.238-.004-.409,0-.782.155-1.019.349-.001,0-.005.003-.006.002-.002,0-.003-.003-.003-.006.003-.027.006-.055.011-.085.07-.374.315-.782.546-1.023.006-.008.014-.015.021-.022s.014-.015.022-.022c.017-.016.034-.031.05-.045.008-.006.017-.013.025-.02.014-.011.027-.022.041-.031.236-.173.573-.303.851-.303h8.235c.333,0,.603.27.603.603Z"
        fill="url(#logo-lg-5)"
      />
      {/* Sparkle / central figure */}
      <path
        className="logo-sparkle"
        d="M23.068,6.972l-1.562.159c-2.024.205-3.937,1.093-4.61,3.171-.089.274-.192,1.132-.192,1.42v11.033c0,3.855-.169,4.685-1.421,6.456-1.182,1.672-3.55,2.623-4.959,2.782-.208.024-.39-.139-.39-.348,0-.129.069-.248.18-.313,1.579-.918,2.447-1.585,3.013-2.494.471-.806.83-1.821,1.002-4.001.073-.924.112-2.059.112-3.476v-3.42c0-2.1-1.703-3.803-3.803-3.803h-6.182c-.423,0-.766-.27-.766-.603s.343-.603.766-.603h8.074c.044,0,.088-.001.131-.004s.086-.005.129-.005h1.015c1.045,0,1.892-.847,1.892-1.892h0c0-.32-.081-.634-.235-.914-.9-1.635-2.561-2.787-4.512-2.985l-1.562-.159c-.02-.002-.02-.032,0-.034l1.562-.158c2.749-.279,4.923-2.453,5.202-5.202l.158-1.562c.002-.02.032-.02.034,0l.158,1.562c.279,2.749,2.453,4.923,5.202,5.202l1.562.158c.02.002.02.032,0,.034Z"
        fill="url(#logo-rg-1)"
      />
    </svg>
  );
};
